import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { COUNTRY_DIAL_CODES } from './countryDialCodes';

interface Country {
  name: string;
  code: string;
  dialCode: string;
}

interface PhoneInputProps {
  value: string;
  onChange: (value: string) => void;
  error?: string;
  disabled?: boolean;
  /** When true, omit the built-in "Teléfono *" label (parent supplies its own). */
  hideLabel?: boolean;
  /** When true, omit the local-number hint under the inputs. */
  hideHint?: boolean;
  /** ISO country of the flag when the field is still empty. Colombia stays the default. */
  defaultCountry?: string;
}

// Emoji flags don't render on Windows (they show "CO"), so flags are images.
const CountryFlag: React.FC<{ code: string }> = ({ code }) => (
  <img
    src={`https://flagcdn.com/${code.toLowerCase()}.svg`}
    alt=""
    loading="lazy"
    className="w-6 h-4 object-cover rounded-sm border border-gray-200 flex-shrink-0"
  />
);

const buildCountries = (): Country[] => {
  let names: { of: (code: string) => string | undefined } | null = null;
  try {
    names = new Intl.DisplayNames(['es'], { type: 'region' });
  } catch (err) {
    console.error('Intl.DisplayNames not available', err);
  }
  return Object.entries(COUNTRY_DIAL_CODES)
    .map(([code, dialCode]) => ({ code, dialCode, name: names?.of(code) || code }))
    .sort((a, b) => a.name.localeCompare(b.name, 'es'));
};

const COUNTRIES = buildCountries();
const DEFAULT_COUNTRY = COUNTRIES.find(c => c.code === 'CO') || COUNTRIES[0];

/** Several countries share a code (+1, +44...): keep the current one, else take the longest match. */
const countryForNumber = (value: string, current: Country | null): Country | undefined => {
  if (current && value.startsWith(current.dialCode)) return current;
  return COUNTRIES
    .filter(c => value.startsWith(c.dialCode))
    .sort((a, b) => b.dialCode.length - a.dialCode.length)[0];
};

const PhoneInput: React.FC<PhoneInputProps> = ({
  value,
  onChange,
  error,
  disabled,
  hideLabel = false,
  hideHint = false,
  defaultCountry = 'CO',
}) => {
  const countryByCode = (code?: string) =>
    COUNTRIES.find(c => c.code === (code || 'CO').toUpperCase()) || DEFAULT_COUNTRY;
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(() => countryByCode(defaultCountry));
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (value) return;
    setSelectedCountry(countryByCode(defaultCountry));
  }, [defaultCountry, value]);

  // Parsear el valor si viene con código de país
  useEffect(() => {
    if (!value) return;
    const country = countryForNumber(value, selectedCountry);
    if (country) {
      if (country !== selectedCountry) setSelectedCountry(country);
      setPhoneNumber(value.slice(country.dialCode.length).trim());
    } else {
      setPhoneNumber(value);
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryChange = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchQuery('');
    // Actualizar el valor completo
    const fullNumber = phoneNumber ? `${country.dialCode}${phoneNumber}` : '';
    onChange(fullNumber);
  };

  // Función para normalizar texto (remover acentos y caracteres especiales)
  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s]/g, ''); 
  };

  const filteredCountries = COUNTRIES.filter(country => {
    const normalizedQuery = normalizeText(searchQuery);
    const normalizedCountryName = normalizeText(country.name);
    const normalizedCode = country.code.toLowerCase();
    
    return (
      normalizedCountryName.includes(normalizedQuery) ||
      country.dialCode.includes(searchQuery) ||
      normalizedCode.includes(normalizedQuery) ||
      normalizedQuery.split(/\s+/).every(word => 
        word.length > 0 && normalizedCountryName.includes(word)
      )
    );
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = e.target.value.replace(/\D/g, '');
    setPhoneNumber(numericValue);
    
    const dialCode = selectedCountry?.dialCode || '+57';
    const fullNumber = numericValue ? `${dialCode}${numericValue}` : '';
    onChange(fullNumber);
  };

  const borderClass = error
    ? 'border-red-300 bg-red-50'
    : 'border-gray-200 focus:border-[#1CB3B3] focus:bg-[#E8F7F7]';

  const fieldBorder = hideLabel
    ? error
      ? 'border border-red-300 bg-red-50 rounded-xl'
      : 'border border-slate-200 bg-slate-50 rounded-xl focus:ring-2 focus:ring-[#1CB3B3]/20 focus:border-[#1CB3B3]'
    : `border-2 rounded-lg ${borderClass}`;

  return (
    <div>
      {!hideLabel && (
        <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700 mb-2">
          Teléfono *
        </label>
      )}
      <div className="relative" ref={dropdownRef}>
        <div className="flex gap-2">
          {/* Selector de país */}
          <button
            type="button"
            onClick={() => !disabled && setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            aria-label={selectedCountry ? `País: ${selectedCountry.name}` : 'Seleccionar país'}
            aria-expanded={isDropdownOpen}
            title={selectedCountry?.name}
            className={`flex items-center gap-2 px-3 py-3 flex-shrink-0 focus:outline-none transition-all duration-200 ${fieldBorder} ${
              disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
            }`}
          >
            {selectedCountry ? (
              <>
                <CountryFlag code={selectedCountry.code} />
                <span className="text-sm font-mono text-gray-700">{selectedCountry.dialCode}</span>
              </>
            ) : (
              <span className="text-sm text-gray-500">...</span>
            )}
            <ChevronDown
              className={`h-4 w-4 text-gray-400 transition-transform ${isDropdownOpen ? 'transform rotate-180' : ''}`}
            />
          </button>

          {/* Número */}
          <input
            id="phone-number"
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            value={phoneNumber}
            onChange={handlePhoneChange}
            placeholder="3123456789"
            disabled={disabled}
            required={hideLabel}
            className={`flex-1 min-w-0 px-4 py-3 focus:outline-none transition-all duration-200 ${fieldBorder} ${
              disabled ? 'opacity-50 cursor-not-allowed' : ''
            }`}
          />
        </div>

        {/* Dropdown de países */}
        {isDropdownOpen && (
          <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden flex flex-col">
            {/* Barra de búsqueda */}
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar país..."
                  autoFocus
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1CB3B3] focus:border-transparent"
                />
              </div>
            </div>

            {/* Lista de países */}
            <div className="overflow-y-auto max-h-64">
              {filteredCountries.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No se encontraron países
                </div>
              ) : (
                filteredCountries.map((country) => (
                  <button
                    key={country.code}
                    type="button"
                    onClick={() => handleCountryChange(country)}
                    className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#E8F7F7] transition-colors ${
                      selectedCountry?.code === country.code ? 'bg-[#E8F7F7]' : ''
                    }`}
                  >
                    <CountryFlag code={country.code} />
                    <span className="flex-1 text-sm font-medium text-gray-700">{country.name}</span>
                    <span className="text-sm text-gray-500 font-mono">{country.dialCode}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
      {error && (
        <p className="text-red-600 text-xs mt-1">{error}</p>
      )}
      {!hideHint && (
        <p className="text-xs text-gray-500 mt-1">
          Ingresa solo el número local (sin código de país)
        </p>
      )}
    </div>
  );
};

export default PhoneInput;
